import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBillingStore } from '../../stores/useBillingStore';
import { useEProtect } from './hooks/useEProtect';
import { getStates } from '../../services/listsApi';
import { isValidCardNumber } from '../../utils/cardValidation';
import { getIsImpersonating } from '../../utils/session';
import type { EProtectSuccessResponse } from '../../types/runtime';

interface StateOption {
  StateAbbr: string;
  StateName: string;
}

export interface BillingFormProps {
  customerServicePhone: string;
  onClose: () => void;
  /** When true, overrides the fixed-drawer CSS so the form renders inline in the page flow. */
  inline?: boolean;
}

function generateYears(): { display: string; id: number }[] {
  const startYear = new Date().getFullYear();
  const years: { display: string; id: number }[] = [];
  for (let i = 0; i < 12; i++) {
    years.push({ display: String(startYear + i), id: startYear + i - 2000 });
  }
  return years;
}

const MONTHS = Array.from({ length: 12 }, (_, i) => ({ id: i + 1 }));

export function BillingForm({ customerServicePhone, onClose, inline = false }: BillingFormProps) {
  const {
    formValues,
    ccHint,
    ccType,
    nyDisclaimerAccepted,
    isProcessing,
    isLoading,
    error,
    submitSuccess,
    loadCardInfo,
    updateField,
    setNYDisclaimer,
    setEprotectResponse,
    submitCardUpdate,
  } = useBillingStore();

  const [states, setStates] = useState<StateOption[]>([]);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const years = useMemo(() => generateYears(), []);
  const cardInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadCardInfo();
    const cached = window.sessionStorage.getItem('statesList');
    if (cached) {
      try {
        setStates(JSON.parse(cached));
      } catch {
        /* ignore */
      }
    } else {
      getStates()
        .then(result => {
          if (result.StatusCode === 'SUCCESS' && Array.isArray(result.States)) {
            setStates(result.States as unknown as StateOption[]);
            window.sessionStorage.setItem('statesList', JSON.stringify(result.States));
          }
        })
        .catch(() => {});
    }
  }, [loadCardInfo]);

  const handleEprotectSuccess = useCallback(
    (response: EProtectSuccessResponse) => {
      setEprotectResponse(response);
      submitCardUpdate({});
    },
    [setEprotectResponse, submitCardUpdate]
  );

  const handleEprotectError = useCallback((_message: string) => {
    // error is displayed via the hook's own error state
  }, []);

  const eprotect = useEProtect(handleEprotectSuccess, handleEprotectError, {
    customerServicePhone,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isProcessing || eprotect.loading) return;
    eprotect.sendToEprotect(cardInputRef.current);
  };

  const handleClose = () => {
    useBillingStore.getState().clearForm();
    onClose();
  };

  const isCardDirty = formValues.CardNumber.length > 0;
  const isCardNumberValid = isCardDirty && isValidCardNumber(formValues.CardNumber);
  const isFormValid =
    !getIsImpersonating() &&
    isCardDirty &&
    isCardNumberValid &&
    formValues.ExpMonth !== '' &&
    formValues.ExpYear !== '' &&
    formValues.cardholderFullName.trim().includes(' ') &&
    formValues.Street1.trim() !== '' &&
    formValues.City.trim() !== '' &&
    formValues.State !== '' &&
    formValues.Zip.length >= 5 &&
    nyDisclaimerAccepted;

  const drawerOverride: React.CSSProperties | undefined = inline
    ? { position: 'static', width: '100%', height: 'auto', boxShadow: 'none', overflow: 'visible', padding: '0' }
    : undefined;

  return (
    <div className="cc-form" style={drawerOverride}>
      <div className="col-md-12">
        <form name="editBillingInfor" onSubmit={handleSubmit}>
          {!inline && (
            <div className="close-cc-form">
              <button
                type="button"
                onClick={handleClose}
                style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
                aria-label="Close"
              >
                <i className="material-icons">close</i>
              </button>
            </div>
          )}
          <fieldset disabled={isProcessing || eprotect.loading}>
            {/* <div className="row">
              <div className="col-lg-12 col-md-12 col-sm-12">
                <h3>Credit Card</h3>
                <hr />
              </div>
            </div> */}

            {isLoading && (
              <div className="spinner">
                <div className="rect1" />
                {' '}
                <div className="rect2" />
                {' '}
                <div className="rect3" />
                {' '}
                <div className="rect4" />
                {' '}
                <div className="rect5" />
                <h4>Checking Card Information</h4>
              </div>
            )}

            {!isLoading && (
              <div className="refill-cc-form-wrapper">
                <div className="row">
                  {/* Card Number */}
                  <div
                    className={`form-group${
                      isCardDirty ? (isCardNumberValid ? ' has-success' : ' has-error') : ''
                    }`}
                  >
                    <label htmlFor="cardNumber">Card Number</label>
                    <input
                      ref={cardInputRef}
                      name="cardNumber"
                      className="form-control"
                      id="cardNumber"
                      type="text"
                      value={formValues.CardNumber}
                      onChange={e => updateField('CardNumber', e.target.value.replace(/\D/g, ''))}
                      placeholder={ccHint}
                      minLength={15}
                      maxLength={16}
                      required
                      disabled={isLoading || isProcessing}
                    />
                    {isCardDirty && isCardNumberValid && ccType && <div className="cc-type">{ccType}</div>}
                    {isCardDirty && !isCardNumberValid && (
                      <span className="help-block error-msg">Please enter a valid credit card number.</span>
                    )}
                  </div>

                  {/* Exp Month / Year */}
                  <div className="row">
                    <div className="form-group col-lg-6 col-md-6 col-sm-6">
                      <label htmlFor="month">Expiration Month</label>
                      <select
                        id="month"
                        className="form-control custom"
                        value={formValues.ExpMonth}
                        onChange={e => updateField('ExpMonth', e.target.value)}
                        required
                        disabled={isLoading || isProcessing}
                      >
                        <option value="">Month</option>
                        {MONTHS.map(m => (
                          <option key={m.id} value={m.id}>
                            {m.id}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group col-lg-6 col-md-6 col-sm-6">
                      <label htmlFor="year">Expiration Year</label>
                      <select
                        id="year"
                        className="form-control custom"
                        value={formValues.ExpYear}
                        onChange={e => updateField('ExpYear', e.target.value)}
                        required
                        disabled={isLoading || isProcessing}
                      >
                        <option value="">Year</option>
                        {years.map(y => (
                          <option key={y.id} value={y.id}>
                            {y.display}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Cardholder Name */}
                  <div className="form-group">
                    <label htmlFor="cardholderFullName">Cardholder Name on Credit Card</label>
                    <input
                      name="cardholderFullName"
                      className="form-control"
                      id="cardholderFullName"
                      type="text"
                      value={formValues.cardholderFullName}
                      onChange={e => updateField('cardholderFullName', e.target.value)}
                      required
                      disabled={isLoading || isProcessing}
                    />
                    {formValues.cardholderFullName &&
                      !formValues.cardholderFullName.trim().includes(' ') && (
                        <span className="help-block error-msg">Please enter a last name</span>
                      )}
                  </div>

                  {/* Billing Address */}
                  <div className="form-group">
                    <h4>Billing Address</h4>
                  </div>
                  <div className="form-group">
                    <label htmlFor="streetOne">Street 1</label>
                    <input
                      name="streetOne"
                      className="form-control"
                      id="streetOne"
                      value={formValues.Street1}
                      onChange={e => updateField('Street1', e.target.value)}
                      required
                      disabled={isLoading || isProcessing}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="streetTwo">Street 2</label>
                    <input
                      name="streetTwo"
                      className="form-control"
                      id="streetTwo"
                      value={formValues.Street2}
                      onChange={e => updateField('Street2', e.target.value)}
                      disabled={isLoading || isProcessing}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="city">City</label>
                    <input
                      name="city"
                      className="form-control"
                      id="city"
                      value={formValues.City}
                      onChange={e => updateField('City', e.target.value)}
                      required
                      disabled={isLoading || isProcessing}
                    />
                  </div>
                  <div className="row">
                    <div className="col-lg-6 col-md-6 col-sm-6">
                      <div className="form-group">
                        <label htmlFor="state">State</label>
                        <select
                          id="state"
                          className="form-control custom"
                          value={formValues.State}
                          onChange={e => updateField('State', e.target.value)}
                          required
                          disabled={isLoading || isProcessing}
                        >
                          <option value="">Pick a state</option>
                          {states.map(s => (
                            <option key={s.StateAbbr} value={s.StateAbbr}>
                              {s.StateName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-6">
                      <div className="form-group">
                        <label htmlFor="zip">Zip</label>
                        <input
                          name="zip"
                          className="form-control"
                          id="zip"
                          value={formValues.Zip}
                          onChange={e =>
                            updateField('Zip', e.target.value.replace(/\D/g, '').slice(0, 5))
                          }
                          minLength={5}
                          maxLength={5}
                          required
                          disabled={isLoading || isProcessing}
                        />
                        {formValues.Zip && formValues.Zip.length < 5 && (
                          <span className="help-block error-msg">
                            Please enter your Zip. MUST be at least 5 digits long.
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <hr />
                  </div>
                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <p>
                      To update your billing address, you will be required to re-enter your credit card information.
                      <br /><br />
                      Updates to your credit card number and/or expiration date will apply only to future orders; pending orders will not be updated automatically.
                      <br /><br />
                      If you'd like pending orders to be charged to your new credit card, please contact Member Services via our live chat feature or by phone at the number listed on your prescription card.
                    </p>
                  </div>
                  <div className="col-lg-12 col-md-12 col-sm-12">
                    <div className="form-group checkbox">
                      <label htmlFor="nyCheck-billing">
                        <input
                          name="nyCheck"
                          id="nyCheck-billing"
                          type="checkbox"
                          checked={nyDisclaimerAccepted}
                          onChange={e => setNYDisclaimer(e.target.checked)}
                          style={{ height: 13 }}
                        />
                        <i>
                          By checking this box, you acknowledge the following: Certain states
                          require that we advise patients about the risk of paying for medical
                          services with a credit card. Medical debts have protections under federal
                          and state laws, specifically the prohibitions against wage garnishment,
                          property liens and reporting of medical debt to credit bureaus. If you pay
                          medical bills with a credit card, those protections may not apply.{' '}
                          <b>
                            By choosing to pay with a credit card, you acknowledge that you were
                            notified of the risks, and you agree to proceed with this payment
                            method.
                          </b>
                        </i>{' '}
                        <a
                          href="https://vytlone.com/wp-content/uploads/2026/02/FAQs-New-York-State-Notice-for-Patients-Paying-by-Credit-Card.pdf"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="NY credit card patient FAQ"
                        >
                          <span style={{ fontWeight: 700, fontSize: '1.15em', lineHeight: 1 }}>
                            &#9432;
                          </span>
                        </a>
                      </label>
                    </div>
                  </div>

                  {/* Success banner — bottom-aligned, above the action row, matching
                      Personal Info's placement (VSDP-5972). The banner no longer replaces
                      the action row with a single "Close" button; per the 2026-07-23 Jira
                      decision, Add/Update Credit Card keeps "Cancel + Save Changes" visible
                      both before and after a successful save so the user can immediately
                      make another edit and save again without exiting the form. */}
                  {submitSuccess && (
                    <div className="row">
                      <div className="col-lg-12 col-md-12 col-sm-12">
                        <div className="alert alert-success" style={{ marginTop: 10 }}>
                          <strong>
                            You have successfully updated your credit card information.
                          </strong>
                        </div>
                      </div>
                    </div>
                  )}

                  {isProcessing || eprotect.loading ? (
                    <div className="spinner" style={inline ? { margin: '20px auto', height: 'auto' } : undefined}>
                      <div className="rect1" style={inline ? { height: 40 } : undefined} />
                      {' '}
                      <div className="rect2" style={inline ? { height: 40 } : undefined} />
                      {' '}
                      <div className="rect3" style={inline ? { height: 40 } : undefined} />
                      {' '}
                      <div className="rect4" style={inline ? { height: 40 } : undefined} />
                      {' '}
                      <div className="rect5" style={inline ? { height: 40 } : undefined} />
                      <h4>Updating Card Info</h4>
                    </div>
                  ) : (
                    <div className="col-lg-12 col-md-12 col-sm-12">
                      <button
                        type="button"
                        className="btn btn-default btn-sm"
                        style={{ backgroundColor: '#FF471A', color: '#fff', borderRadius: 4, border: 'none' }}
                        onClick={() => {
                          if (useBillingStore.getState().isDirty()) {
                            setShowCancelConfirm(true);
                          } else {
                            handleClose();
                          }
                        }}
                      >
                        Cancel
                      </button>
                      {'  '}
                      <button
                        type="submit"
                        disabled={!isFormValid}
                        className="btn btn-primary btn-sm"
                        style={{ float: 'right' }}
                      >
                        Save Changes
                      </button>
                    </div>
                  )}
                </div>

                {(error || eprotect.error) && (
                  <div className="row">
                    <div className="col-lg-12 col-md-12 col-sm-12">
                      <div className="alert alert-danger" style={{ marginTop: 10 }}>
                        {error || eprotect.error}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </fieldset>
        </form>
      </div>

      {/* Unsaved-changes confirmation dialog — mirrors the WizardStep3Payment / RefillsPage sidebar guard */}
      {showCancelConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100%',
            width: '100%',
            background: 'rgba(0, 0, 0, 0.7)',
            zIndex: 1040,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="billing-form-unsaved-dialog-title"
          onKeyDown={e => {
            if (e.key === 'Escape') {
              setShowCancelConfirm(false);
            }
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 8,
              boxShadow: '0 3px 3px rgba(0, 0, 0, .3)',
              padding: 25,
              width: 550,
              maxWidth: '90%',
              textAlign: 'center',
            }}
          >
            <h3
              id="billing-form-unsaved-dialog-title"
              style={{ textTransform: 'uppercase', fontWeight: 'bold', marginTop: 0 }}
            >
              Unsaved Changes
            </h3>
            <p style={{ marginBottom: 15 }}>
              The form contains unsaved changes, are you sure you want to leave this screen?
            </p>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 10,
                paddingBottom: 15,
                borderBottom: '5px solid #FF471A',
              }}
            >
              <button
                type="button"
                style={{
                  backgroundColor: '#FF471A',
                  color: '#FAFAFA',
                  border: 'none',
                  borderRadius: 4,
                  padding: '8px 20px',
                  cursor: 'pointer',
                }}
                onClick={() => setShowCancelConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{
                  backgroundColor: '#FFD24C',
                  color: '#1E1E1E',
                  border: 'none',
                  borderRadius: 4,
                  padding: '8px 20px',
                  minWidth: 75,
                  cursor: 'pointer',
                }}
                onClick={() => {
                  setShowCancelConfirm(false);
                  handleClose();
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
